import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import DropDownSelect from "../../../../connectui/component/input/dropdown/DropDownSelect";
import OutlinedDropDownBase from "../../../../connectui/component/input/dropdown/base/OutlinedDropDownBase";
import Text from "../../../../connectui/component/display/Text";
import Container from "../../../../connectui/component/layout/Container";
import VirtualizedTableComponent from "../../../../connectui/component/layout/table/VirtualizedTableComponent";
import {getDashboardRevenue} from "redux/actions/dashboardActions";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import Tooltip from "../../../../connectui/component/display/Tooltip";
import ModalBase from "../../../../connectui/component/modal/ModalBase";

import ButtonIconText from "../../../../connectui/component/input/button/ButtonIconText";
import EmptyButtonBase from "../../../../connectui/component/input/button/base/EmptyButtonBase";
import {useTranslation} from "react-i18next";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import Divider from "../../../../connectui/component/display/Divider";

const DISPLAY_TYPES = {
  "By Product Name": "product_name",
  "By Product Type": "product_type",
};

const RevenuePerformanceCard = ({
  timeframe,
  currency = "USD",
  listingGroups = null,
}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const [displayType, setDisplayType] = useState("By Product Name");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const {t} = useTranslation();
  const revenueData = useSelector(
    (state) => state.dashboardReducer.revenueData,
  );

  const fetchData = () => {
    if (!timeframe) return;

    setLoading(true);
    setError(false);

    let isMounted = true;

    dispatch(
      getDashboardRevenue({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: listingGroups,
        display_currency: currency,
        stat: DISPLAY_TYPES[displayType],
        onSuccess: (response) => {
          if (isMounted) {
            setLoading(false);
          }
        },
        onError: (error) => {
          if (isMounted) {
            setLoading(false);
            setError(true);
          }
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    return fetchData();
  }, [
    timeframe?.startDate,
    timeframe?.endDate,
    displayType,
    currency,
    listingGroups,
  ]);

  useEffect(() => {
    if (revenueData?.product_types) {
      setData(revenueData.product_types);
    } else {
      setData([]);
    }
  }, [revenueData]);

  const formatCurrency = (value) => {
    const safeCurrency = currency || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: safeCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleViewMore = () => {
    setModalOpen(true);
  };

  const renderEmptyState = (message, height = "200px") => (
    <DashboardEmptyStateCard
      text={message}
      icon={ICONS["no-products-alt"]}
      height={height}
    />
  );

  const renderBottomBar = () => (
    <FlexContainer
      justifyContent="center"
      alignItems={"center"}
      flexDirection="column"
      sx={{width: "100%"}}
    >
      <Divider />
      <ButtonIconText
        buttonBase={EmptyButtonBase}
        label={"View More"}
        onClick={handleViewMore}
        color={theme.colors.primary}
        as="div"
        sx={{
          padding: theme.spacing(1, 2),
          color: theme.colors.primary,
          fontSize: theme.typography.textSM.fontSize,
          fontWeight: theme.typography.textSM.medium.fontWeight,
          "&:hover": {
            backgroundColor: theme.colors.primary50,
          },
        }}
        icon={
          <SvgIcon
            component={ICONS["arrow-up-right"]}
            sx={{
              width: "20px",
              height: "20px",
              marginRight: theme.spacing(0.5),
            }}
          />
        }
      />
    </FlexContainer>
  );

  const renderCardTitle = () => (
    <FlexContainer alignItems="center" gap={theme.spacing(1)}>
      <Text variant={"textMD"} color={"textSecondary"} weight={"medium"}>
        {t("dashboard.revenue-performance.title")}
      </Text>
      <Tooltip tooltipContent={t("dashboard.revenue-performance.help")}>
        <Container sx={{cursor: "help"}}>
          <SvgIcon
            component={ICONS["help-circle"]}
            sx={{
              width: "14px",
              height: "14px",
              color: theme.colors.gray400,
            }}
          />
        </Container>
      </Tooltip>
    </FlexContainer>
  );

  const tableColumns = [
    {
      label:
        displayType === "By Product Name" ? "Product Name" : "Product Type",
      field: "product_type",
      sortable: true,
      render: (row) => (
        <FlexContainer alignItems="center" justifyContent={"flex-start"}>
          <Tooltip
            tooltipContent={row.product_type}
            sx={{
              tooltip: {
                maxWidth: "300px",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
              trigger: {
                display: "block",
                width: "100%",
              },
            }}
          >
            <Text
              variant="textSM"
              weight="medium"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
            >
              {row.product_type}
            </Text>
          </Tooltip>
        </FlexContainer>
      ),
      getSortValue: (row) => row.product_type?.toLowerCase() || "",
    },
    {
      label: "# Sold",
      field: "num_of_purchases",
      sortable: true,
      width: "80px",
      render: (row) => (
        <FlexContainer alignItems="center" justifyContent={"flex-start"}>
          <Text variant="textSM">{row.num_of_purchases}</Text>
        </FlexContainer>
      ),
      getSortValue: (row) => row.num_of_purchases || 0,
    },
    {
      label: "Revenue",
      field: "revenue",
      sortable: true,
      width: "100px",
      render: (row) => (
        <FlexContainer alignItems="center" justifyContent={"flex-start"}>
          <Text variant="textSM" weight="medium">
            {formatCurrency(row.revenue)}
          </Text>
        </FlexContainer>
      ),
      getSortValue: (row) => row.revenue || 0,
    },
  ];

  const renderTableView = (isModal = false) => {
    if (loading) {
      return (
        <Container
          skeleton={{
            enabled: true,
            width: "100%",
            height: isModal ? "400px" : "200px",
          }}
          sx={{width: "100%"}}
        />
      );
    }

    if (data.length === 0 || error) {
      return renderEmptyState(
        t("dashboard.revenue-performance.empty-state"),
        isModal ? "400px" : "200px",
      );
    }

    const displayData = isModal ? data : data.slice(0, 4);
    const tableHeight = isModal ? "400px" : "150px";

    return (
      <Container sx={{height: tableHeight, width: "100%"}}>
        <VirtualizedTableComponent
          columns={tableColumns}
          data={displayData}
          labelProps={{variant: "textSM"}}
          rowStyles={{borderBottom: "none"}}
          tableStyles={{
            border: "none",
            tableLayout: "fixed",
            width: "100%",
          }}
          headerStyles={{backgroundColor: "transparent", height: "37px"}}
          horizontalScroll={false}
          totalRows={displayData.length}
          rowHeight={37}
          isRowLoaded={({index}) => index < displayData.length}
          loadMoreRows={() => {}}
          defaultSortField="revenue"
          defaultSortDirection="DESC"
          gutters={theme.spacing(0)}
        />
      </Container>
    );
  };

  const renderCardContent = () => (
    <FlexContainer
      width={"100%"}
      alignItems={"flex-start"}
      flexDirection={"column"}
      gap={theme.spacing(4)}
    >
      <FlexContainer
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"100%"}
      >
        {renderCardTitle()}
        <FlexContainer gap={theme.spacing(2)} alignItems="center">
          <DropDownSelect
            dropDownBase={OutlinedDropDownBase}
            label={displayType}
            selected={displayType}
            sx={{
              label: {
                padding: theme.spacing(1.5, 3.5),
                minWidth: "120px",
              },
            }}
            onChange={setDisplayType}
          >
            <FlexContainer value="By Product Name" label="By Product Name">
              <Text color={"textPrimary"}>By Product Name</Text>
            </FlexContainer>
            <FlexContainer value="By Product Type" label="By Product Type">
              <Text color={"textPrimary"}>By Product Type</Text>
            </FlexContainer>
          </DropDownSelect>
        </FlexContainer>
      </FlexContainer>

      {renderTableView()}
      {!loading && !error && data.length > 0 && renderBottomBar()}
    </FlexContainer>
  );

  return (
    <>
      <SimpleCard
        customContent={renderCardContent()}
        sx={{
          height: "100%",
          width: "100%",
          padding: theme.spacing(5, 5, 3, 5),
          backgroundColor: theme.colors.white,
        }}
      />

      <ModalBase
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          padding: theme.spacing(6),
          maxWidth: "800px",
          width: "90%",
          maxHeight: "90vh",
        }}
      >
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(3)}
          sx={{height: "100%"}}
        >
          <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            {renderCardTitle()}
            <FlexContainer alignItems="center" gap={theme.spacing(3)}>
              <DropDownSelect
                dropDownBase={OutlinedDropDownBase}
                label={displayType}
                selected={displayType}
                sx={{
                  label: {
                    padding: theme.spacing(1.5, 3.5),
                    minWidth: "120px",
                  },
                }}
                onChange={setDisplayType}
              >
                <FlexContainer value="By Product Name" label="By Product Name">
                  <Text color={"textPrimary"}>By Product Name</Text>
                </FlexContainer>
                <FlexContainer value="By Product Type" label="By Product Type">
                  <Text color={"textPrimary"}>By Product Type</Text>
                </FlexContainer>
              </DropDownSelect>
              <SvgIcon
                component={ICONS.x}
                onClick={() => setModalOpen(false)}
                sx={{
                  cursor: "pointer",
                  width: "24px",
                  height: "24px",
                  color: theme.colors.gray500,
                }}
              />
            </FlexContainer>
          </FlexContainer>
          {renderTableView(true)}
        </FlexContainer>
      </ModalBase>
    </>
  );
};

export default RevenuePerformanceCard;
